import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import type { RouteKey } from "@/lib/routes";

interface IndexCardListProps {
  items: RouteKey[];
  imageMap?: Partial<Record<RouteKey, string>>;
  testIdPrefix?: string;
}

/**
 * Card list used on parent index pages (Services, Commodities, Compliance,
 * Resources). Editorial, indexed, hover-reveal, no invented copy.
 */
export function IndexCardList({ items, imageMap, testIdPrefix = "index-item" }: IndexCardListProps) {
  const { t, path } = useT();

  return (
    <ul className="border-t border-bone-500" data-testid={`${testIdPrefix}-list`}>
      {items.map((key, idx) => {
        const meta = t.routes[key] as { title: string; label: string; eyebrow?: string; lede?: string };
        const img = imageMap?.[key];
        return (
          <li key={key} className="border-b border-bone-500 group">
            <Link
              to={path(key)}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-8 md:py-10 items-center hover:bg-bone-300/40 transition-colors -mx-4 md:-mx-6 px-4 md:px-6"
              data-testid={`${testIdPrefix}-${key.replace(".", "-")}`}
            >
              <div className="md:col-span-1 font-mono text-[10px] tracking-eyebrow text-slate2 uppercase">
                {String(idx + 1).padStart(2, "0")}
              </div>

              <div className={img ? "md:col-span-6" : "md:col-span-8"}>
                <h3 className="font-display font-light text-3xl md:text-4xl text-ink leading-tight group-hover:text-clay transition-colors">
                  {meta.title}
                </h3>
                {meta.lede && (
                  <p className="mt-3 text-slate2 max-w-prose-tight leading-relaxed">
                    {meta.lede}
                  </p>
                )}
              </div>

              {img && (
                <div className="md:col-span-4 aspect-[4/3] overflow-hidden bg-ink/5">
                  <img
                    src={img}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </div>
              )}

              <div className="md:col-span-1 flex md:justify-end">
                <ArrowUpRight
                  size={20}
                  className="text-ink group-hover:text-clay transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  aria-hidden
                />
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
